<script lang="ts" setup>
import { TASK_STATE } from "@core/constants"
import { useTaskStore } from "@core/stores/task.store"
import { useI18n } from "vue-i18n"
import { onUnmounted, ref } from "vue"

const taskStore = useTaskStore()

/** État fictif pour les titres i18n : succès technique + flag métier FAILURE (sans « échoué »). */
const TASK_TITLE_STATE_INFORMATIONAL = "informational"

const { t, te } = useI18n({
    messages: {
        fr: {
            tasks: {
                task_export_buildings_sources: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const state = named("state")

                    if (state === TASK_TITLE_STATE_INFORMATIONAL) {
                        return "Export des données du parc"
                    }
                    if (state === TASK_STATE.SUCCESS) {
                        return "Export des données du parc terminé"
                    } else if (state === TASK_STATE.FAILURE) {
                        return "Export des données du parc échoué"
                    }

                    return "Export des données du parc..."
                },
                task_export_rt: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const state = named("state")

                    if (state === TASK_TITLE_STATE_INFORMATIONAL) {
                        return "Export des données du référentiel technique"
                    }
                    if (state === TASK_STATE.SUCCESS) {
                        return "Export des données terminé"
                    } else if (state === TASK_STATE.FAILURE) {
                        return "Export des données échoué"
                    }

                    return "Export des données du référentiel technique..."
                },
                workers2app_getlastdata_updateapp_request: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const state = named("state")

                    if (state === TASK_TITLE_STATE_INFORMATIONAL) {
                        return "Récupération des dernières données"
                    }
                    if (state === TASK_STATE.SUCCESS) {
                        return "Récupération des dernières données terminée"
                    } else if (state === TASK_STATE.FAILURE) {
                        return "Récupération des dernières données échouée"
                    }

                    return "Récupération des dernières données en cours..."
                },
            },
            task_default: "Export en cours...",
            download: "Télécharger le fichier",
            /** Attente par défaut d’une tâche longue qui n’émet aucune étape. */
            waiting_default:
                "Traitement en cours. Cela peut prendre plusieurs minutes.",
            waiting_elapsed_under_minute: "Lancé il y a moins d'une minute.",
            waiting_elapsed_minutes: "Lancé il y a {minutes} min.",
            download_blocked:
                "Votre navigateur a bloqué l'ouverture automatique du fichier. Cliquez ci-dessous pour le récupérer.",
            progress_steps:
                "Étape {current} sur {total} réalisée. Veuillez patienter...",
            success_url:
                "Le fichier est téléchargé automatiquement. Cliquer ici si ce n'est pas le cas.",
            success_task: "L'opération a été réalisée avec succès.",
            /** Échec pour les tâches d’export (`task_export_*`). */
            failure_task_export:
                "L'export n'a pas pu être réalisé. Veuillez réessayer plus tard si le problème persiste.",
            /** Échec lorsque la tâche n’a pas de `taskToastFailureMessage` et n’est pas un export. */
            failure_task_generic:
                "L'opération n'a pas pu être réalisée. Veuillez réessayer plus tard si le problème persiste.",
            failure_raison: "Raison de l'échec :",
            informational_detail_label: "Information :",
            failure_progress_bar: "Erreur",
            error: {
                "aucune-donnee": "Aucune donnée à exporter",
            },
        },
    },
})

/** Succès technique (`status` OK) + `flag` métier FAILURE : pas un échec d’exécution. */
function isInformationalOutcomeToast(taskId: string): boolean {
    const task = taskStore.getTaskById(taskId)
    if (!task || task.state !== TASK_STATE.SUCCESS) return false
    const ret = task.return
    return ret?.flag === "FAILURE" || ret?.result?.flag === "FAILURE"
}

/** Affichage du bloc « fin de tâche » type erreur (vrai échec ou information métier). */
function isFailureLikeToast(taskId: string): boolean {
    const task = taskStore.getTaskById(taskId)
    if (!task) return false
    if (task.state === TASK_STATE.FAILURE) return true
    return isInformationalOutcomeToast(taskId)
}

function isExportTaskName(taskname: string | undefined): boolean {
    if (!taskname) return false
    return taskname.startsWith("task_export")
}

/** Horloge basse fréquence : seule source de réactivité du temps écoulé. */
const nowMs = ref(Date.now())
const elapsedTimer = window.setInterval(() => {
    nowMs.value = Date.now()
}, 15_000)
onUnmounted(() => window.clearInterval(elapsedTimer))

/** Tâche encore en cours (ni succès, ni échec, ni information métier). */
function isRunningToast(taskId: string): boolean {
    const task = taskStore.getTaskById(taskId)
    if (!task) return false
    if (isFailureLikeToast(taskId)) return false
    return ![TASK_STATE.SUCCESS, TASK_STATE.FAILURE, TASK_STATE.EMPTY].includes(
        task.state ?? ""
    )
}

/**
 * Vrai si la tâche publie un avancement réel (sous-tâches, étapes ou libellé
 * d'étape). Sinon la barre chiffrée est une fiction : `task-started` la fige à
 * 25 % pendant toute la durée du traitement.
 */
function hasRealProgressSignal(taskId: string): boolean {
    const task = taskStore.getTaskById(taskId)
    if (!task) return false
    if (typeof task.subtasks_total === "number" && task.subtasks_total > 0) {
        return true
    }
    if ((task.progress_tasks?.length ?? 0) > 0) return true
    return Boolean(task.current_step_message)
}

/** Barre indéterminée quand la tâche tourne sans aucun avancement mesurable. */
function isIndeterminateProgress(taskId: string): boolean {
    return isRunningToast(taskId) && !hasRealProgressSignal(taskId)
}

/** Message d'attente affiché tant qu'aucune étape n'est publiée. */
function getWaitingMessage(taskId: string): string {
    if (!isIndeterminateProgress(taskId)) return ""
    const task = taskStore.getTaskById(taskId)
    return task?.taskToastWaitingMessage || t("waiting_default")
}

/** Temps écoulé depuis le lancement : seul indice que le travail avance encore. */
function getElapsedLabel(taskId: string): string {
    if (!isRunningToast(taskId)) return ""
    const startedAt = taskStore.getTaskById(taskId)?.startedAt
    if (!startedAt) return ""
    const minutes = Math.floor((nowMs.value - startedAt) / 60_000)
    return minutes < 1
        ? t("waiting_elapsed_under_minute")
        : t("waiting_elapsed_minutes", { minutes })
}

/** URL du fichier produit : champ dédié, sinon format legacy `return.data.file_url`. */
function getDownloadUrl(taskId: string): string {
    const task = taskStore.getTaskById(taskId)
    const url = task?.downloadUrl ?? task?.return?.data?.file_url
    return typeof url === "string" ? url : ""
}

/** Ouverture automatique bloquée par le navigateur : le bouton devient le seul recours. */
function isDownloadAutoOpenBlocked(taskId: string): boolean {
    return Boolean(taskStore.getTaskById(taskId)?.downloadAutoOpenBlocked)
}

/** Masque étapes / message de succès sous la barre (option tâche). */
function getTaskHideSubtitle(taskId: string): boolean {
    return Boolean(taskStore.getTaskById(taskId)?.taskToastHideSubtitle)
}

function getTaskName(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    // 1. Valeur personnalisée depuis la tâche
    if (task?.taskToastTitle) {
        return task.taskToastTitle
    }
    const taskname = task?.taskname
    const state = isInformationalOutcomeToast(taskId)
        ? TASK_TITLE_STATE_INFORMATIONAL
        : task?.state
    // 2. Clé i18n enregistrée pour le taskname
    const i18nKey = taskname?.replace(/\./g, "_")
    if (i18nKey && te(`tasks.${i18nKey}`)) {
        return t(`tasks.${i18nKey}`, {
            state,
        })
    }
    // 3. Valeur par défaut
    return t("task_default")
}

/**
 * Avancement affiché : la valeur retenue par le store, qui la recalcule à chaque event et
 * ne la laisse jamais redescendre (`task-progress.helper`). Le toast ne calcule plus rien
 * lui-même : deux formules pour une seule barre finissaient par se contredire.
 */
function getTaskProgressByState(taskId: string) {
    return taskStore.getTaskById(taskId)?.progress_percent ?? 0
}

/** Dernière sous-tâche portant une vraie mesure `step` sur `nsteps`. */
function getLastMeasuredStep(taskId: string) {
    const progressTasks = taskStore.getTaskById(taskId)?.progress_tasks ?? []
    const measured = progressTasks.filter(
        (pt) => pt.nsteps != null && pt.nsteps > 0 && pt.step != null
    )
    return measured.length > 0 ? measured[measured.length - 1] : null
}

/**
 * Détail sous-tâches (format Celery avec `subtasks_total`) ou message d’étape parent.
 */
function getSubtaskProgressLine(taskId: string): string {
    const task = taskStore.getTaskById(taskId)
    if (!task) return ""
    const completed = (task.progress_tasks ?? []).filter(
        (pt) => pt.state === TASK_STATE.SUCCESS
    ).length

    const total = task.subtasks_total

    if (typeof total === "number" && total > 0) {
        const inProgress = [...(task.progress_tasks ?? [])]
            .reverse()
            .find((pt) => pt.state === TASK_STATE.PROGRESS)
        const label =
            inProgress?.message ||
            inProgress?.label ||
            inProgress?.label ||
            task.current_step_message ||
            ""
        const base = `${completed} / ${total}`
        return label ? `${base} — ${label}` : ""
    }
    if ((task.progress_tasks?.length ?? 0) > 0) {
        const inProgress = [...(task.progress_tasks ?? [])]
            .reverse()
            .find((pt) => pt.state === TASK_STATE.PROGRESS)
        const label =
            inProgress?.message ||
            inProgress?.label ||
            inProgress?.label ||
            inProgress?.sub_taskname ||
            task.current_step_message ||
            ""
        if (!label) return ""
        // Une tâche qui publie `step` / `nsteps` compte ses étapes, pas ses sous-tâches
        // terminées : préfixer par `completed` afficherait « 0 — » pendant tout l'export.
        return getLastMeasuredStep(task.task_id) ? label : `${completed} — ${label}`
    }
    if (task.current_step_message && task.state === TASK_STATE.PROGRESS) {
        return task.current_step_message
    }
    return ""
}

/**
 * Détail d’erreur affiché sous le message générique : message API / Pusher en priorité, puis code i18n.
 */
function getErrorReason(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    const ret = task?.return
    const direct = ret?.message ?? ret?.data?.message
    if (typeof direct === "string" && direct.trim() !== "") {
        return direct.trim()
    }
    const code = ret?.error?.code ?? ret?.data?.detail?.code

    if (code && te(`error.${code}`)) {
        return t(`error.${code}`)
    }
    return ""
}

function isTaskError(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    return task?.state === TASK_STATE.FAILURE
}

/**
 * Texte principal du bloc d’erreur (vrai FAILURE uniquement) : personnalisé, sinon export vs générique.
 */
function getFailurePrimaryLine(taskId: string): string {
    const task = taskStore.getTaskById(taskId)
    if (!task || task.state !== TASK_STATE.FAILURE) return ""
    if (task.taskToastFailureMessage) {
        return task.taskToastFailureMessage
    }
    if (isExportTaskName(task.taskname)) {
        return t("failure_task_export")
    }
    return t("failure_task_generic")
}

function getDetailCaption(taskId: string): string {
    return isInformationalOutcomeToast(taskId)
        ? t("informational_detail_label")
        : t("failure_raison")
}

/**
 * Message de succès affiché dans le toast : personnalisé (taskToastSuccessMessage) ou défaut i18n
 */
function getSuccessMessage(taskId: string) {
    const task = taskStore.getTaskById(taskId)
    if (task?.taskToastSuccessMessage) {
        return task.taskToastSuccessMessage
    }
    return t("success_task")
}

function onClose(taskId: string, callback: () => void) {
    callback()

    taskStore.removeTask(taskId)
}
</script>

<template>
    <BaseToast group="tasks" position="top-right" class="task-toast" closable>
        <template #container="{ message, closeCallback }">
            <div class="task-toast-content">
                <!-- Titre de la tâche et bouton fermer -->
                <div class="task-toast-content__header">
                    <h3 class="text-lg font-bold">
                        {{ getTaskName(message.detail) }}
                    </h3>
                    <BaseButton
                        icon="pi pi-times"
                        rounded
                        variant="text"
                        @click="onClose(message.detail, closeCallback)"
                    />
                </div>
                <!-- Barre de progression -->
                <BaseProgressBar
                    v-if="
                        !isFailureLikeToast(message.detail) &&
                        ![TASK_STATE.EMPTY, TASK_STATE.FAILURE].includes(
                            taskStore.getTaskById(message.detail)?.state ?? ''
                        )
                    "
                    :mode="
                        isIndeterminateProgress(message.detail)
                            ? 'indeterminate'
                            : 'determinate'
                    "
                    :value="
                        isIndeterminateProgress(message.detail)
                            ? undefined
                            : getTaskProgressByState(message.detail)
                    "
                >
                    <span v-if="isTaskError(message.detail)">
                        {{ t("failure_progress_bar") }}
                    </span>
                </BaseProgressBar>
                <!-- Attente honnête : aucune étape n'est publiée, on annonce la durée et le temps écoulé -->
                <div
                    v-if="getWaitingMessage(message.detail)"
                    class="task-toast-content__waiting"
                >
                    <p class="text-sm">
                        {{ getWaitingMessage(message.detail) }}
                    </p>
                    <p
                        v-if="getElapsedLabel(message.detail)"
                        class="text-sm text-surface-600"
                    >
                        {{ getElapsedLabel(message.detail) }}
                    </p>
                </div>
                <p
                    v-if="
                        !getTaskHideSubtitle(message.detail) &&
                        getSubtaskProgressLine(message.detail)
                    "
                    class="text-sm text-surface-600"
                >
                    {{ getSubtaskProgressLine(message.detail) }}
                </p>
                <div
                    v-if="
                        !getTaskHideSubtitle(message.detail) &&
                        isRunningToast(message.detail) &&
                        getLastMeasuredStep(message.detail)?.nsteps
                    "
                >
                    <p class="text-sm">
                        {{
                            t("progress_steps", {
                                current:
                                    getLastMeasuredStep(message.detail)?.step ||
                                    0,
                                total:
                                    getLastMeasuredStep(message.detail)
                                        ?.nsteps || 0,
                            })
                        }}
                    </p>
                </div>
                <!-- URL de téléchargement -->
                <div
                    class="task-toast-content__url"
                    v-if="getDownloadUrl(message.detail)"
                >
                    <p class="font-medium text-sm">
                        {{
                            isDownloadAutoOpenBlocked(message.detail)
                                ? t("download_blocked")
                                : t("success_url")
                        }}
                    </p>
                    <BaseButton
                        as="a"
                        :label="t('download')"
                        target="_blank"
                        size="small"
                        :href="getDownloadUrl(message.detail)"
                    />
                </div>
                <!-- Message de succès -->
                <!-- Le message de succès annonce l'ouverture du fichier : il
                     contredirait le bloc ci-dessus quand elle a été bloquée. -->
                <div
                    v-if="
                        !getTaskHideSubtitle(message.detail) &&
                        taskStore.getTaskById(message.detail)?.state ===
                            TASK_STATE.SUCCESS &&
                        !isFailureLikeToast(message.detail) &&
                        !isDownloadAutoOpenBlocked(message.detail)
                    "
                >
                    <p class="font-medium text-sm">
                        {{ getSuccessMessage(message.detail) }}
                    </p>
                </div>
                <!-- Vrai échec ou information métier (status OK + flag FAILURE) : pas de libellé « échoué » pour ce dernier cas -->
                <div
                    v-if="
                        taskStore.getTaskById(message.detail)?.state ===
                            TASK_STATE.FAILURE ||
                        isFailureLikeToast(message.detail)
                    "
                >
                    <p
                        v-if="getFailurePrimaryLine(message.detail)"
                        class="font-medium text-sm"
                    >
                        {{ getFailurePrimaryLine(message.detail) }}
                    </p>
                    <p
                        v-if="getErrorReason(message.detail)"
                        class="font-semibold"
                    >
                        {{ getDetailCaption(message.detail) }}<br />
                        <span class="font-medium text-sm">{{
                            getErrorReason(message.detail)
                        }}</span>
                    </p>
                </div>
            </div>
        </template>
    </BaseToast>
</template>

<style scoped>
.task-toast-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    .task-toast-content__url {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
    .task-toast-content__waiting {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }
    .task-toast-content__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
}
</style>
<style>
.task-toast-task-finished-failure {
    --p-toast-info-background: var(--p-toast-error-background) !important;
    --p-toast-info-border-color: var(--p-toast-error-border-color) !important;
    --p-toast-info-color: var(--p-toast-error-color) !important;
    --p-progressbar-value-background: var(
        --p-toast-error-border-color
    ) !important;
}
</style>
